import { ChevronLeftIcon } from '@interstate/components/Icons';
import { useDispatch } from 'react-redux';

import { useAppRouter } from '@makemydeal/dr-sales-checkin-ui-utils';
import { SCI_CDL_TAGS } from '@makemydeal/dr-sales-checkin-types';
import { setHamburgerVisibility } from '@makemydeal/dr-sales-checkin-store';
import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';

import SideBarNav from '../SideBarNav/SideBarNav';
import { BackButton, PageLayoutBody, PageLayoutContainer, PageLayoutRoot, SidebarLayoutContainer } from './pageLayout.style';
import type { PageLayoutProps } from '../../types';
import { BACK_BTN_LABEL, ShowroomLayout } from '../../constants';

/**
 * PageLayout
 *
 * Shared shell that renders one of two chrome variants around routed content:
 * - `sidebar`: showroom-style layout with the left navigation.
 * - `back`: standalone page with a blue background and a "Back to My Deal"
 *   button (e.g. Manage Documents).
 *
 * The variant is route-driven (see `useShowroomLayoutIdentifier`) so the shell stays a
 * pure function of the URL.
 */
export const PageLayout = ({ children, variant = ShowroomLayout.BACK, backLabel = BACK_BTN_LABEL }: PageLayoutProps) => {
    const { goBack } = useAppRouter();
    const dispatch = useDispatch();

    const handleBack = () => {
        dispatch({ type: CDL_V3_EVENT, payload: SCI_CDL_TAGS['dc-sci-098']() });
        dispatch(setHamburgerVisibility(true));
        goBack();
    };

    if (variant === ShowroomLayout.SIDEBAR) {
        return (
            <PageLayoutRoot data-testid="page-layout">
                <SidebarLayoutContainer>
                    <SideBarNav />
                    {children}
                </SidebarLayoutContainer>
            </PageLayoutRoot>
        );
    }

    return (
        <PageLayoutRoot data-testid="page-layout">
            <PageLayoutContainer>
                <BackButton type="button" onClick={handleBack} data-testid="page-layout-back-button">
                    <ChevronLeftIcon />
                    {backLabel}
                </BackButton>
                <PageLayoutBody data-testid="page-layout-body">{children}</PageLayoutBody>
            </PageLayoutContainer>
        </PageLayoutRoot>
    );
};

export default PageLayout;
